import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  MapPin, 
  Users, 
  Target, 
  Clock, 
  Route, 
  Calendar,
  Activity,
  AlertCircle,
  X
} from "lucide-react";
import { toast } from "sonner";
import { useForm, useApi, useFetch } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { validateRequired, validateDistance, validateMaxLength } from "../utils/validation";
import { useAuth } from "../contexts/AuthContext";
import { parseDateTimeForForm, convertTo24HourFormat, convertLocalToUTC, getUserTimezone } from "../utils/dateTimeUtils";
import { convertImagePositionToFloat, DEFAULT_IMAGE_POSITION } from "../utils/dataTransformers";
import { compressImageForUpload } from "../utils/imageCompression";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const EventCreationForm = ({ onClose, onEventCreated, editingRun = null, isEditing = false }) => {
  const { user } = useAuth();
  
  // Fetch club data to get club's city for auto-filling
  const { data: clubData, loading: clubLoading } = useFetch(
    () => apiService.clubs.getById(user?.club_id),
    [user?.club_id],
    { enabled: !!user?.club_id }
  );
  
  // Parse datetime for separate date and time fields (convert UTC to meeting timezone)
  const parsedDateTime = parseDateTimeForForm(editingRun?.scheduled_date, editingRun?.timezone || null);

  // Use our new form hook with validation
  const form = useForm(
    {
      name: editingRun?.name || "",
      what_to_expect: editingRun?.what_to_expect || "",
      datetime: editingRun?.scheduled_date ? (() => {
        // Convert UTC to meeting timezone for form display
        const { convertUTCToMeetingTimezone } = require("../utils/dateTimeUtils");
        const meetingDate = convertUTCToMeetingTimezone(editingRun.scheduled_date, editingRun?.timezone || null);
        if (meetingDate) {
          const year = meetingDate.getFullYear();
          const month = String(meetingDate.getMonth() + 1).padStart(2, '0');
          const day = String(meetingDate.getDate()).padStart(2, '0');
          const hours = String(meetingDate.getHours()).padStart(2, '0');
          const minutes = String(meetingDate.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        return new Date(editingRun.scheduled_date).toISOString().slice(0, 16);
      })() : "",
      address: editingRun?.address || "",
      city: editingRun?.city || "",
      difficulty: editingRun?.difficulty || "",
      event_type: editingRun?.event_type || "",
      recurrence: editingRun?.is_recurring ? editingRun?.recurrence_pattern || "weekly" : "one-time",
      image: editingRun?.image || "",
      imageFile: null,
      imagePosition: editingRun?.imagePosition || DEFAULT_IMAGE_POSITION
    },
    {
      name: (value) => {
        const requiredError = validateRequired(value, 'Run Name');
        if (requiredError) return requiredError;
        const maxLengthError = validateMaxLength(value, 30, 'Run Name');
        if (maxLengthError) return maxLengthError;
        return null;
      },
      what_to_expect: (value) => validateRequired(value, 'Run Notes'),
      datetime: (value) => validateRequired(value, 'Date and time'),
      difficulty: (value) => validateRequired(value, 'Difficulty'),
      event_type: (value) => validateRequired(value, 'Event type')
    }
  );


  // Image positioning state - initialize from form values
  const [imagePosition, setImagePosition] = useState(form.values.imagePosition || DEFAULT_IMAGE_POSITION); // Percentage values
  
  const [isPositioning, setIsPositioning] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Use our API hook for form submission
  const { execute: executeSubmit, loading: submitLoading } = useApi();
  
  // Use our API hook for deleting meetings
  const { execute: executeDelete, loading: deleteLoading } = useApi();

  // State for delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Sync imagePosition with form state
  useEffect(() => {
    form.setValues(prev => ({
      ...prev,
      imagePosition: imagePosition
    }));
  }, [imagePosition]);

  // Auto-fill city field with club's city when club data is loaded
  useEffect(() => {
    if (clubData && !isEditing) {
      // Only auto-fill for new events, not when editing existing ones
      form.setValues(prev => ({
        ...prev,
        city: clubData.city || ""
      }));
    }
  }, [clubData, isEditing]);
  
  // Simple time state management
  const [selectedHour, setSelectedHour] = useState(parsedDateTime.hour);
  const [selectedMinute, setSelectedMinute] = useState(parsedDateTime.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(parsedDateTime.period);

        
        const updateDateTime = () => {
          if (!selectedHour || !selectedMinute || !selectedPeriod) return;
          
          const date = form.values.datetime ? form.values.datetime.split('T')[0] : new Date().toISOString().split('T')[0];
          const hour24 = convertTo24HourFormat(parseInt(selectedHour), selectedPeriod);
          
          const datetimeString = `${date}T${hour24.toString().padStart(2, '0')}:${selectedMinute}`;
          handleInputChange('datetime', datetimeString);
        };
        
        // Update datetime when any time component changes
        useEffect(() => {
          updateDateTime();
        }, [selectedHour, selectedMinute, selectedPeriod]);

  const [errors, setErrors] = useState({});
  // Remove old loading state - now handled by useApi hook

  // Set up distance fields when editing
  // Note: This useEffect is intentionally empty - distance fields are handled elsewhere
  React.useEffect(() => {
    if (isEditing && editingRun) {
      // Distance fields setup handled in form initialization
    }
  }, [isEditing, editingRun]);

  // Handle date and time initialization when editing
  React.useEffect(() => {
    if (isEditing && editingRun?.scheduled_date) {
      try {
        const scheduledDate = new Date(editingRun.scheduled_date);
        if (!isNaN(scheduledDate.getTime())) {
          // Extract date and time from the stored date
          const dateString = scheduledDate.toISOString().split('T')[0]; // YYYY-MM-DD format
          const timeString = scheduledDate.toTimeString().slice(0, 5); // HH:MM format
          
          form.setValues(prev => ({
            ...prev,
            date: dateString,
            time: timeString
          }));
        }
      } catch (error) {
        // Silently handle date parsing errors
      }
    }
  }, [isEditing, editingRun]);

  // Handle image initialization when editing
  React.useEffect(() => {
    if (isEditing && editingRun?.image) {
      // Use the image directly - it's already a base64 data URL or full URL
      form.setValues(prev => ({
        ...prev,
        image: editingRun.image
      }));
    }
  }, [isEditing, editingRun]);

  // Preview component
  const RunCardPreview = () => {
    const formatDateTime = (datetimeString) => {
      if (!datetimeString) return "Select date and time";

      
      // Get user's timezone for preview (since we're showing what user selected)
      const { getUserTimezone } = require("../utils/dateTimeUtils");
      const userTimezone = getUserTimezone();
      
      // Convert the datetime string to a Date object
      const date = new Date(datetimeString);
      
      // Format in user's timezone (since this is what they're selecting)
      return {
        date: date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric',
          timeZone: userTimezone
        }),
        time: date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: userTimezone
        })
      };
    };

    return (
      <div className="rounded-3xl overflow-hidden shadow-2xl border-2 border-black relative">
        <div 
          className="relative h-[300px] overflow-hidden"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: isPositioning ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {form.values.image || (isEditing && editingRun?.image) ? (
            <img 
              src={form.values.image || editingRun?.image} 
              alt={form.values.name || "Run preview"}
              className="w-full h-full object-cover select-none"
              style={{
                objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                pointerEvents: isPositioning ? 'none' : 'auto'
              }}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full spray-paint-placeholder">
            </div>
          )}
          
          {/* Time Until - Top Left */}
          {form.values.datetime && !isPositioning && (
            <div className="absolute top-4 left-4">
              <span className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium border border-white/20">
                Upcoming
              </span>
            </div>
          )}

          {/* Difficulty Badge - Top Right */}
          {!isPositioning && (
            <div className="absolute top-4 right-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium border border-white/20 ${
                form.values.difficulty === 'Beginner' ? 'bg-green-600/60 backdrop-blur-sm text-white' :
                form.values.difficulty === 'Intermediate' ? 'bg-yellow-600/60 backdrop-blur-sm text-white' :
                form.values.difficulty === 'Advanced' ? 'bg-red-600/60 backdrop-blur-sm text-white' :
                'bg-gray-600/60 backdrop-blur-sm text-white'
              }`}>
                {form.values.difficulty || 'All Levels'}
              </span>
            </div>
          )}

          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        </div>

        {/* Information Section */}
        <div className="bg-white">
          {/* Basic Info Section */}
          <div className="p-3">
            {/* Event Name */}
            <h3 className="text-sm font-bold text-black mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
              {form.values.name || "Your Run Name"}
            </h3>
            
            {/* Run Notes */}
            <p className="text-gray-700 text-xs mb-2 leading-tight" style={{fontFamily: 'Montserrat, sans-serif'}}>
              {form.values.what_to_expect || "Describe your run - pace, distance range, workout details, etc."}
            </p>
            
            {/* Event Details */}
            <div className="flex flex-col space-y-0.5 mb-2">
              {form.values.datetime && (
                <div className="flex items-center text-gray-600">
                  <Clock className="w-3 h-3 mr-1" />
                  <span className="text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    {formatDateTime(form.values.datetime).time}
                  </span>
                </div>
              )}
              <div className="flex items-center text-gray-600">
                <Users className="w-3 h-3 mr-1" />
                <span className="text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  +0 Going
                </span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="w-3 h-3 mr-1" />
                <span className="text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {form.values.address || "Select address"}
                </span>
              </div>
              {form.values.datetime && (
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-3 h-3 mr-1" />
                  <span className="text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    {formatDateTime(form.values.datetime).date}
                  </span>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-center gap-2">
              <button 
                className="w-20 bg-black text-white px-2 py-2 rounded-full font-semibold text-xs hover:bg-gray-800 transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-500" 
                style={{fontFamily: 'Montserrat, sans-serif'}}
                disabled
              >
                Join Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const eventTypes = [
    { value: "5K", label: "5K Run", icon: Route, description: "5 kilometer run" },
    { value: "10K", label: "10K Run", icon: Route, description: "10 kilometer run" },
    { value: "Half Marathon", label: "Half Marathon", icon: Route, description: "21.1 kilometer run" },
    { value: "Marathon", label: "Marathon", icon: Route, description: "42.2 kilometer run" },
    { value: "Fun Run", label: "Fun Run", icon: Activity, description: "Casual, non-competitive run" },
    { value: "Training", label: "Training Run", icon: Target, description: "Structured training session" },
    { value: "Social", label: "Social Run", icon: Users, description: "Community building run" }
  ];

  const difficultyLevels = [
    { value: "Beginner", label: "Beginner" },
    { value: "Intermediate", label: "Intermediate" },
    { value: "Advanced", label: "Advanced" }
  ];

  const recurrenceOptions = [
    { value: "one-time", label: "One-time Event", description: "Single occurrence" },
    { value: "daily", label: "Daily", description: "Every day" },
    { value: "weekly", label: "Weekly", description: "Every week" },
    { value: "monthly", label: "Monthly", description: "Every month" }
  ];

  const handleInputChange = (field, value) => {
    form.setValues(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };


  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          image: "Please select a valid image file"
        }));
        return;
      }

      // Validate file size (max 10MB for better performance)
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          image: "Image size must be less than 10MB"
        }));
        return;
      }

        try {
          setIsUploading(true);
          
          // Clear any previous errors first
          setErrors(prev => ({
            ...prev,
            image: ""
          }));

          // Compress image before upload
          const compressedFile = await compressImageForUpload(file, 'event');
          
          // Upload compressed image to GCP Cloud Storage
          const response = await apiService.upload.image(compressedFile, 'event');
          
          const imageUrl = response.data.url;
          
          form.setValues(prev => ({
            ...prev,
            image: imageUrl, // Store the GCP Cloud Storage URL
            imageFile: file
          }));
          
          // Reset image position when new image is uploaded
          setImagePosition(DEFAULT_IMAGE_POSITION);
        } catch (error) {
          // Upload error
          setErrors(prev => ({
            ...prev,
            image: "Failed to upload image. Please try again."
          }));
        } finally {
          setIsUploading(false);
        }
    }
  };

  // Image positioning handlers
  const handleMouseDown = (e) => {
    if (!isPositioning) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isPositioning) return;
    
    const newX = Math.max(0, Math.min(100, ((e.clientX - dragStart.x) / e.currentTarget.offsetWidth) * 100));
    const newY = Math.max(0, Math.min(100, ((e.clientY - dragStart.y) / e.currentTarget.offsetHeight) * 100));
    
    setImagePosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const startPositioning = () => {
    setIsPositioning(true);
  };

  const savePosition = () => {
    setIsPositioning(false);
  };

  const validateForm = () => {
    // Use the form's built-in validation
    return form.validate();
  };

  const handleDelete = async () => {
    if (!editingRun?.id) {
      toast.error("No meeting to delete");
      return;
    }

    // Check if meeting is recurring
    const isRecurring = editingRun?.is_recurring || false;

    if (isRecurring) {
      // Show dialog with options for recurring meetings
      setShowDeleteDialog(true);
    } else {
      // For non-recurring meetings, show simple confirmation
      if (!window.confirm('Are you sure you want to delete this meeting? This action cannot be undone.')) {
        return;
      }

      try {
        await executeDelete(
          () => apiService.meetings.delete(editingRun.id, false),
          { 
            showSuccessToast: true,
            successMessage: 'Meeting deleted successfully'
          }
        );
        onEventCreated(); // This will close the form and refresh the data
      } catch (error) {
        // Error is already handled by useApi hook
      }
    }
  };

  const executeDeleteWithOption = async (deleteEntireSeries = false) => {
    if (!editingRun?.id) return;

    try {
      await executeDelete(
        () => apiService.meetings.delete(editingRun.id, deleteEntireSeries),
        { 
          showSuccessToast: true,
          successMessage: deleteEntireSeries 
            ? 'Meeting series deleted successfully'
            : 'Meeting deleted successfully'
        }
      );
      setShowDeleteDialog(false);
      onEventCreated(); // This will close the form and refresh the data
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Force validation on all fields by triggering blur on each required field
    const requiredFields = ['name', 'what_to_expect', 'datetime', 'address', 'difficulty', 'event_type'];
    requiredFields.forEach(field => {
      form.handleBlur(field);
    });
    
    // Use our form validation
    if (!form.validate()) {
      // Collect all validation errors and show specific missing fields
      const validationErrors = form.errors;
      const missingFields = [];
      
      // Check each required field
      const requiredFields = [
        { key: 'name', label: 'Run Name' },
        { key: 'what_to_expect', label: 'Run Notes' },
        { key: 'datetime', label: 'Date and Time' },
        { key: 'address', label: 'Event Address' },
        { key: 'difficulty', label: 'Difficulty Level' },
        { key: 'event_type', label: 'Event Type' }
      ];
      
      requiredFields.forEach(field => {
        let isEmpty = false;
        
        if (field.key === 'datetime') {
          // For datetime, check if we have both date and time components
          const hasDate = form.values.datetime && form.values.datetime.split('T')[0];
          const hasTime = selectedHour && selectedMinute && selectedPeriod;
          isEmpty = !hasDate || !hasTime;
        } else {
          isEmpty = !form.values[field.key] || (typeof form.values[field.key] === 'string' && form.values[field.key].trim() === '');
        }
        
        if (isEmpty) {
          missingFields.push(field.label);
        }
      });
      
      if (missingFields.length > 0) {
        const errorMessage = missingFields.length === 1 
          ? `Please fill in the required field: ${missingFields[0]}`
          : `Please fill in the following required fields: ${missingFields.join(', ')}`;
        toast.error(errorMessage);
        return;
      }
      
      // If we get here, there might be other validation errors
      toast.error("Please fix the errors below");
      return;
    }

    try {
      // Get user's timezone and convert local time to UTC
      const userTimezone = getUserTimezone();
      const localDate = new Date(form.values.datetime);
      const utcString = convertLocalToUTC(localDate, userTimezone);
      
      // Prepare meeting data as JSON
      const baseMeetingData = {
        name: form.values.name,
        what_to_expect: form.values.what_to_expect,
        meeting_type: form.values.recurrence === "one-time" ? "one_time" : "recurring",
        scheduled_date: utcString,
        user_timezone: userTimezone,  // Send user's timezone so backend can convert properly
        address: form.values.address || "",
        city: form.values.city || clubData?.city || "",
        state: clubData?.state || "",
        difficulty: form.values.difficulty,
        event_type: form.values.event_type,
        is_recurring: form.values.recurrence !== "one-time",
        recurrence_pattern: form.values.recurrence === "one-time" ? null : form.values.recurrence,
        image: form.values.image || null,
        image_position: convertImagePositionToFloat(form.values.imagePosition)
      };

      // For new meetings, include creation fields
      const meetingData = isEditing ? baseMeetingData : {
        ...baseMeetingData,
        club_id: user.club_id,
        created_by: user.id
      };

      let response;
      if (isEditing && editingRun) {
        
        // Check if we have a valid ID
        if (!editingRun.id) {
          toast.error('Unable to edit: Missing meeting ID');
          return;
        }
        
        
        // Update existing meeting
        response = await executeSubmit(
          () => apiService.meetings.update(editingRun.id, meetingData),
          { 
            showSuccessToast: true,
            successMessage: 'Event updated successfully!'
          }
        );
      } else {
        // Create new meeting
        response = await executeSubmit(
          () => apiService.meetings.create(meetingData),
          { 
            showSuccessToast: true,
            successMessage: 'Event created successfully!'
          }
        );
      }
      
      // Call the callback to close the form and refresh events
      if (onEventCreated) {
        onEventCreated(response.data);
      }
      
      // Reset form using our form hook
      form.reset();
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      // Error already handled by useApi hook
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-black border border-white/20 rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                {isEditing ? "Edit Run" : "Schedule a Run"}
              </h2>
              <p className="text-white/80" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {isEditing ? "Update your running event details" : "Create a new running event for your club"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-0 focus:border-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Section */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Event Image */}
                <div className="space-y-2">
                  <Label htmlFor="image" className="text-white font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Event Image {!isEditing && "*"}
                  </Label>
                  {isEditing && (
                    <p className="text-white/60 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Leave empty to keep current image
                    </p>
                  )}
                  <div className="relative">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="bg-white/10 border-white/30 text-white file:bg-white/20 file:border-white/30 file:text-white file:rounded-md file:px-3 file:py-1 file:mr-3 file:text-sm file:hover:bg-white/30 file:focus:bg-white/40 file:focus:border-2 file:focus:border-white file:focus:ring-4 file:focus:ring-white file:focus:shadow-xl file:focus:shadow-white/50 focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20 disabled:opacity-50"
                    />
                    {isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md">
                        <div className="flex items-center space-x-2 text-white">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.image && (
                    <p className="text-red-400 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.image}
                    </p>
                  )}
                  <p className="text-white/80 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>Upload an image file (max 10MB, JPG/PNG/GIF)</p>
                </div>

                {/* Image Positioning Controls */}
                {(form.values.image || (isEditing && editingRun?.image)) && (
                  <div className="space-y-3 p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1">
                        <Label className="text-white font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          Adjust Image Position
                        </Label>
                        <p className="text-white/70 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          {isPositioning ? 'Drag the image to position it, then click Save Position' : 'Click to start positioning your image'}
                        </p>
                      </div>
                      <div className="flex gap-2 sm:flex-shrink-0 w-full sm:w-auto">
                        {!isPositioning ? (
                          <Button
                            type="button"
                            onClick={startPositioning}
                            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto"
                          >
                            Position Image
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            onClick={savePosition}
                            className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto"
                          >
                            Save Position
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {isPositioning && (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <p className="text-blue-300 text-sm text-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
                          🖱️ Click and drag the image in the preview above to position it
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Run Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white font-medium">
                Run Name *
              </Label>
              <Input
                id="name"
                value={form.values.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Saturday Morning 5K"
                maxLength={30}
                className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20"
              />
              {errors.name && (
                <p className="text-red-400 text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Run Notes */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-white font-medium">
                Run Notes *
              </Label>
              <Textarea
                id="what_to_expect"
                value={form.values.what_to_expect}
                onChange={(e) => handleInputChange('what_to_expect', e.target.value)}
                placeholder="Describe your run event here - pace, distance range, workout outline, etc."
                className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 min-h-[100px]"
              />
              {errors.what_to_expect && (
                <p className="text-red-400 text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.what_to_expect}
                </p>
              )}
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date" className="text-white font-medium">
                  Event Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={form.values.datetime ? form.values.datetime.split('T')[0] : ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    if (selectedHour && selectedMinute && selectedPeriod) {
                      // If time is selected, create full datetime
                      const hour24 = convertTo24HourFormat(parseInt(selectedHour), selectedPeriod);
                      
                      const datetimeString = `${date}T${hour24.toString().padStart(2, '0')}:${selectedMinute}`;
                      handleInputChange('datetime', datetimeString);
                    } else {
                      // If no time selected, just set the date part
                      const currentTime = form.values.datetime ? form.values.datetime.split('T')[1] : '00:00';
                      const datetimeString = `${date}T${currentTime}`;
                      handleInputChange('datetime', datetimeString);
                    }
                  }}
                  className="bg-white/10 border-white/30 text-white focus:border-white/50 focus:ring-0 focus:outline-none [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time" className="text-white font-medium">
                  Event Time *
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={selectedHour}
                    onValueChange={(hour) => setSelectedHour(hour)}
                  >
                    <SelectTrigger className="bg-white/10 border-white/30 text-white focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20">
                      <SelectValue placeholder="Hour" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 text-black border border-white/20">
                      {Array.from({length: 12}, (_, i) => i + 1).map(hour => (
                        <SelectItem 
                          key={hour} 
                          value={hour.toString()} 
                          className="text-black hover:bg-blue-100 hover:text-blue-900 cursor-pointer transition-all duration-200 focus:bg-blue-100 focus:text-blue-900 data-[highlighted]:bg-blue-100 data-[highlighted]:text-blue-900"
                        >
                          {hour}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={selectedMinute}
                    onValueChange={(minute) => setSelectedMinute(minute)}
                  >
                    <SelectTrigger className="bg-white/10 border-white/30 text-white focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20">
                      <SelectValue placeholder="Min" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 text-black border border-white/20">
                      {['00', '15', '30', '45'].map(minute => (
                        <SelectItem 
                          key={minute} 
                          value={minute} 
                          className="text-black hover:bg-blue-100 hover:text-blue-900 cursor-pointer transition-all duration-200 focus:bg-blue-100 focus:text-blue-900 data-[highlighted]:bg-blue-100 data-[highlighted]:text-blue-900"
                        >
                          {minute}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={selectedPeriod}
                    onValueChange={(period) => setSelectedPeriod(period)}
                  >
                    <SelectTrigger className="bg-white/10 border-white/30 text-white focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20">
                      <SelectValue placeholder="AM/PM" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 text-black border border-white/20">
                      <SelectItem 
                        value="AM" 
                        className="text-black hover:bg-blue-100 hover:text-blue-900 cursor-pointer transition-all duration-200 focus:bg-blue-100 focus:text-blue-900 data-[highlighted]:bg-blue-100 data-[highlighted]:text-blue-900"
                      >
                        AM
                      </SelectItem>
                      <SelectItem 
                        value="PM" 
                        className="text-black hover:bg-blue-100 hover:text-blue-900 cursor-pointer transition-all duration-200 focus:bg-blue-100 focus:text-blue-900 data-[highlighted]:bg-blue-100 data-[highlighted]:text-blue-900"
                      >
                        PM
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {(errors.datetime || errors.date || errors.time) && (
              <p className="text-red-400 text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.datetime || errors.date || errors.time}
              </p>
            )}

            {/* Difficulty and Event Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white font-medium">Difficulty Level *</Label>
                <div className="grid grid-cols-3 gap-2">
                  {difficultyLevels.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => handleInputChange('difficulty', level.value)}
                      className={`p-2 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                        form.values.difficulty === level.value
                          ? 'bg-white/20 text-white border-white/30'
                          : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-medium text-xs">{level.label}</div>
                      </div>
                    </button>
                  ))}
                </div>
                {errors.difficulty && (
                  <p className="text-red-400 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.difficulty}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_type" className="text-white font-medium">
                  Event Type *
                </Label>
                <Select value={form.values.event_type} onValueChange={(value) => handleInputChange('event_type', value)}>
                  <SelectTrigger className="bg-white/10 border-white/30 text-white focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="social">Social Run</SelectItem>
                    <SelectItem value="charity">Charity Run</SelectItem>
                    <SelectItem value="fun">Fun Run</SelectItem>
                  </SelectContent>
                </Select>
                {errors.event_type && (
                  <p className="text-red-400 text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.event_type}
                  </p>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address" className="text-white font-medium">
                Full Address
              </Label>
              <Input
                id="address"
                value={form.values.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="e.g., 123 Main St, City, State 12345"
                className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20"
              />
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label htmlFor="city" className="text-white font-medium">
                City
              </Label>
              <Input
                id="city"
                value={form.values.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="e.g., New York"
                className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 focus:border-white focus:ring-2 focus:ring-white/50 focus:outline-none focus:shadow-lg focus:shadow-white/20"
              />
              {clubData && (
                <p className="text-white/60 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Auto-filled from your club's location
                </p>
              )}
            </div>

            {/* Distance and Max Participants - Removed as not needed */}

            {/* Recurrence */}
            <div className="space-y-2">
              <Label className="text-white font-medium">Recurrence *</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {recurrenceOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('recurrence', option.value)}
                    className={`p-3 rounded-lg border transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                      form.values.recurrence === option.value
                        ? 'bg-white/20 text-white border-white/30'
                        : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <div className="font-medium text-sm">{option.label}</div>
                    <div className="text-xs text-gray-400 mt-1">{option.description}</div>
                  </button>
                ))}
              </div>
              {errors.recurrence && (
                <p className="text-red-400 text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.recurrence}
                </p>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 flex-1"
                disabled={submitLoading || deleteLoading}
              >
                Cancel
              </Button>
              {isEditing && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white flex-1"
                  disabled={submitLoading || deleteLoading}
                >
                  {deleteLoading ? "Deleting..." : "Delete Run"}
                </Button>
              )}
              <Button
                type="submit"
                className="bg-white text-black hover:bg-gray-100 flex-1"
                disabled={submitLoading || deleteLoading}
              >
                {submitLoading ? (isEditing ? "Updating Run..." : "Creating Run...") : (isEditing ? "Update Run" : "Schedule Run")}
              </Button>
            </div>
          </form>
            </div>

            {/* Preview Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>Runcard Preview</h3>
                <p className="text-white/80 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>See how your run will appear to connections</p>
              </div>
              <div className="w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="sm:col-span-2 lg:col-span-2">
                    <div className="scale-110">
                      <RunCardPreview />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Meeting Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Meeting</AlertDialogTitle>
            <AlertDialogDescription>
              {editingRun?.is_recurring
                ? 'This is a recurring meeting. Would you like to delete just this meeting or the entire series?'
                : 'Are you sure you want to delete this meeting? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {editingRun?.is_recurring ? (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => executeDeleteWithOption(false)}
                  className="bg-gray-500 hover:bg-gray-600"
                >
                  Delete this meeting only
                </AlertDialogAction>
                <AlertDialogAction
                  onClick={() => executeDeleteWithOption(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete entire series
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => executeDeleteWithOption(false)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Yes, Delete
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default EventCreationForm;
